import {type} from 'arktype'

import UserFriendlyError from '@/utils/errors/UserFriendlyError'
import {useAtomEffect} from '@/utils/jotai/useAtomEffect'
import simpleStorage from '@/utils/simpleStorage'

import {countryAtom, getCurrency, isSupportedCountry, SupportedCountries} from './country'
import {isSupportedLanguage, languageAtom, SupportedLanguages} from './language'

export const SupportedLocales = type(`'${SupportedLanguages}_${SupportedCountries}'`)
export type SupportedLocales = `${SupportedLanguages}_${SupportedCountries}`

export function localeUnderscoreToHyphen(locale: SupportedLocales) {
  return locale.replace('_', '-')
}

export function isUserSelectedLocale() {
  return simpleStorage.get('isSelectedLocale')
}

export function setUserSelectedLocale() {
  simpleStorage.set('isSelectedLocale', true)
}

export function isSupportedLocale(locale: unknown): locale is SupportedLocales {
  if (typeof locale !== 'string') return false
  const [language, country] = locale.split('_')
  return isSupportedLanguage(language) && isSupportedCountry(country)
}

export function getLanguageCountryFromLocale(locale: SupportedLocales) {
  const [language, country] = locale.split('_') as [SupportedLanguages, SupportedCountries]
  return {
    language,
    country,
  }
}

export function getCurrencyFromLocale(locale: SupportedLocales) {
  const country = locale.split('_')[1]

  if (!isSupportedCountry(country)) {
    throw new UserFriendlyError('Unsupported country', new Error()).permanent()
  }

  const currency = getCurrency(country)

  if (!currency) {
    throw new UserFriendlyError('Unsupported country', new Error()).permanent()
  }

  return currency.toLowerCase()
}

export const localeAtom = atom(
  get => {
    const country = get(countryAtom)
    const language = get(languageAtom)
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- the type assertion is necessary because the type is not inferred correctly
    return `${language}_${country}` as SupportedLocales
  },
  (_, set, locale: SupportedLocales) => {
    const [language, country] = locale.split('_')
    set(languageAtom, language as SupportedLanguages)
    set(countryAtom, country as SupportedCountries)
    setUserSelectedLocale()
  },
)

export function useLocale() {
  return useAtom(localeAtom)
}

export function useLocaleEffect(localeDispatch: Dispatch<SetStateAction<SupportedLocales>>) {
  useAtomEffect(localeAtom, localeDispatch)
}
